/* eslint-disable react/function-component-definition */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  GoogleMap,
  withScriptjs,
  withGoogleMap,
  Marker,
} from 'react-google-maps';
import { FaRegBuilding, FaWhatsapp, FaGlobe } from 'react-icons/fa';
import { FiPhone, FiMail, FiExternalLink } from 'react-icons/fi';
import AppHeader from '../../shared/components/app-header/AppHeader';
import { getStringFromLS } from '../../shared/helper-utils/localStorage';
import { APP_USER_AUTH } from '../../saas/AppStorageStaticNames';
import {
  getStaticPageContent,
  sendEnquiryEmailApi,
} from '../../api/profileApi';
import { isEmpty, scrollToBottom } from '../../shared/helper-utils/appUtils';
import AppLoading from '../../shared/components/app-loading/AppLoading';
import AppSelect from '../../shared/components/app-select/AppSelect';
import AppAlert from '../../shared/components/app-alert/AppAlert';
import './ContactUsPage.scss';
import supportAgentImg from './contact_agent_illustration.png';

const MAP_KEY = 'AIzaSyDh1Zoc7hX7m6iaTii9if3ANmt_7JqzY9Y';

const OFFICES = [
  {
    id: 'bengaluru',
    city: 'Bengaluru',
    tag: 'HEAD OFFICE',
    address: [
      '1112, 11th Floor, Barton Centre,',
      'Mahatma Gandhi Road, Ashok Nagar,',
      'Bangalore, Karnataka 560001',
    ],
    lat: 12.9756044,
    lng: 77.6067332,
    mapsUrl:
      'https://maps.google.com/?q=Fastays+Barton+Centre+MG+Road+Bangalore',
  },
  {
    id: 'delhi',
    city: 'Delhi',
    tag: 'REGIONAL OFFICE',
    address: ['4/18, Second Floor,', 'Patel Nagar, East Delhi 110008'],
    lat: 28.6517,
    lng: 77.1581,
    mapsUrl: 'https://maps.google.com/?q=4/18+Patel+Nagar+East+Delhi+110008',
  },
  {
    id: 'mumbai',
    city: 'Mumbai',
    tag: 'REGIONAL OFFICE',
    address: [
      'Door No 512, 93 Palladian, Chakala',
      'Andheri East, Mumbai, Maharashtra 400056',
    ],
    lat: 19.116405101028096,
    lng: 72.86167202883638,
    mapsUrl:
      'https://maps.google.com/?q=Door+No+512,+93+Palladian,+Chakala,+Andheri+East,+Mumbai+400093',
  },
];

const ContactMapComponent = withScriptjs(
  withGoogleMap(({ lat, lng, name }) => (
    <GoogleMap
      defaultZoom={14}
      center={{ lat, lng }}
      defaultCenter={{ lat, lng }}
      defaultOptions={{ disableDefaultUI: true }}
    >
      <Marker position={{ lat, lng }} title={name} />
    </GoogleMap>
  )),
);

const ContactLocationMap = ({ lat, lng, name }) => (
  <div style={{ height: '100%', width: '100%', position: 'relative' }}>
    <ContactMapComponent
      lat={lat}
      lng={lng}
      googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${MAP_KEY}&v=3.exp&libraries=geometry,drawing,places`}
      loadingElement={<div style={{ height: '100%' }} />}
      containerElement={<div style={{ height: '100%' }} />}
      mapElement={<div style={{ height: '100%' }} />}
      name={name}
    />
  </div>
);

ContactLocationMap.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
};

function ContactUsPage() {
  const userAuth = getStringFromLS(APP_USER_AUTH);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(undefined);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedOfficeIndex, setSelectedOfficeIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    service: '',
    querymessage: '',
    agreeToTerms: false,
  });
  const [openFaqIndex, setOpenFaqIndex] = useState();

  const [searchParams] = useSearchParams();

  const currentOffice = OFFICES[selectedOfficeIndex];

  const getPageContent = async () => {
    setIsLoading(true);
    await getStaticPageContent('contact_us');
    if (searchParams.get('scrollToForm')) {
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getPageContent();
  }, []);

  const handleUpdateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSelectService = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    handleUpdateField('service', value);
  };

  const validateForm = () => {
    const errors = {};
    if (isEmpty(formData.name)) {
      errors.name = 'Name is required';
    }
    if (isEmpty(formData.email)) {
      errors.email = 'Email id is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (isEmpty(formData.number)) {
      errors.number = 'Mobile number is required';
    }
    if (isEmpty(formData.service)) {
      errors.service = 'Please choose a service';
    }
    if (isEmpty(formData.querymessage)) {
      errors.querymessage = 'Message is required';
    }
    if (!formData.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the Terms of Service';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    const apiPayload = {
      name: formData.name,
      email: formData.email,
      number: formData.number,
      enquirytype: formData.service,
      querymessage: formData.querymessage,
    };
    const response = await sendEnquiryEmailApi(apiPayload);
    if (response && response.ApiStatus && response.ApiStatus.Status === 'OK') {
      setAlert({
        variant: 'success',
        title: '',
        description:
          'Our team has received your query, we will get back to you shortly',
      });
      setFormData({
        name: '',
        email: '',
        number: '',
        service: '',
        querymessage: '',
        agreeToTerms: false,
      });
      setValidationErrors({});
    } else {
      setAlert({
        variant: 'error',
        title: '',
        description:
          'Our team is busy right now, please raise your enquiry later',
      });
    }
    setIsLoading(false);
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? -1 : index);
  };

  const serviceOptions = [
    { label: 'Hotels Booking', value: 'hotels' },
    { label: 'Flights Booking', value: 'flights' },
    { label: 'Cabs Booking', value: 'cabs' },
    { label: 'Buses Booking', value: 'buses' },
    { label: 'Cruise Booking', value: 'cruise' },
    { label: 'Visa Services', value: 'visa' },
    { label: 'Esim Services', value: 'esim' },
    { label: 'Group Booking', value: 'group_booking' },
    { label: 'Other', value: 'other' },
  ];

  const faqs = [
    {
      question: "What's the fastest way to reach Fastays?",
      answer:
        'Calling or WhatsApping one of the numbers above gets you a live response the fastest. The contact form and email are best for non-urgent or detailed queries.',
    },
    {
      question: 'What are your support hours?',
      answer:
        'Our customer support is available 24/7 for bookings and urgent issue resolution. General query assistance is available from Monday to Saturday, 9:30 AM to 6:30 PM.',
    },
    {
      question: 'Do you offer WhatsApp support?',
      answer:
        'Yes, we offer dedicated WhatsApp support at +91 91876 96392 for quick chat assistance and booking updates.',
    },
    {
      question: 'How long does it take to hear back after I submit the form?',
      answer:
        'We typically respond to form enquiries within 2-4 business hours during our standard office hours.',
    },
    {
      question: 'Can I visit your Bengaluru office without an appointment?',
      answer:
        'We recommend scheduling an appointment beforehand to ensure the right representative is available to assist you. However, you are always welcome during business hours.',
    },
    {
      question: 'Which languages can I get support in?',
      answer:
        'We offer support in English, Hindi, and Kannada to assist our diverse client base.',
    },
  ];

  return (
    <>
      {isLoading && <AppLoading isLoading={isLoading} />}
      {alert && (
        <AppAlert
          showIcon
          variant={alert.variant}
          title={alert.title}
          description={alert.description}
          onHide={() => setAlert(undefined)}
        />
      )}
      {(userAuth === null || userAuth === undefined) && <AppHeader />}

      <div className="contact-us-page">
        {/* Header Hero Section */}
        <section className="contact-hero">
          <div className="hero-text">
            <span className="hero-tag">Contact Fastays</span>
            <h1 className="hero-title">
              We&apos;re One Call, Chat or Visit Away.
            </h1>
            <p className="hero-subtitle">
              Reach our team in Bengaluru, Delhi, or Mumbai for bookings,
              partnerships or any query you have — pick whatever&apos;s fastest
              for you.
            </p>
          </div>
          <div className="hero-image-container">
            <img src={supportAgentImg} alt="Support Agent Illustration" />
          </div>
        </section>

        {/* Core 3-Column Layout */}
        <div className="contact-main-card">
          {/* Info Column */}
          <div className="info-column">
            {/* City Selector Tabs */}
            <div className="city-selector-tabs">
              {OFFICES.map((office, idx) => (
                <button
                  key={office.id}
                  type="button"
                  className={`city-tab ${
                    selectedOfficeIndex === idx ? 'active' : ''
                  }`}
                  onClick={() => setSelectedOfficeIndex(idx)}
                >
                  {office.city}
                </button>
              ))}
            </div>

            <div className="office-header">
              <div className="office-icon">
                <FaRegBuilding />
              </div>
              <span className="office-tag">{currentOffice.tag}</span>
            </div>
            <h2 className="office-city">{currentOffice.city}</h2>
            <p className="office-address">
              {currentOffice.address.map((line, i) => (
                <React.Fragment key={line}>
                  {line}
                  {i < currentOffice.address.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            <div className="section-divider" />
            <h3 className="get-in-touch-label">Get in Touch</h3>

            {OFFICES[selectedOfficeIndex].id === 'bengaluru' && (
              <div className="contact-methods">
                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FiPhone />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Call us</div>
                    <div className="item-value">
                      <a href="tel:+919187696392" className="phone-number">
                        +91 91876 96390
                      </a>
                      <a href="tel:+919187696394" className="phone-number">
                        +91 91876 96392
                      </a>
                      <a href="tel:+918197810030" className="phone-number">
                        +91 81978 10030
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FiMail />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Email us</div>
                    <div className="item-value">
                      <a href="mailto:info@fastays.com">info@fastays.com</a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaWhatsapp />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">WhatsApp</div>
                    <div className="item-value">
                      <a
                        href="https://wa.me/919187696392"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        +91 91876 96392
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaGlobe />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Website</div>
                    <div className="item-value">
                      <a
                        href="https://agents.fastays.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        agents.fastays.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {OFFICES[selectedOfficeIndex].id === 'delhi' && (
              <div className="contact-methods">
                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FiPhone />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Call us</div>
                    <div className="item-value">
                      <a href="tel:+919187696392" className="phone-number">
                        +91 91876 96393
                      </a>
                      <a href="tel:+919187696394" className="phone-number">
                        +91 91876 96392
                      </a>
                      <a href="tel:+918197810030" className="phone-number">
                        +91 81978 10030
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FiMail />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Email us</div>
                    <div className="item-value">
                      <a href="mailto:ops.delhi@fastays.com">
                        {' '}
                        ops.delhi@fastays.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaWhatsapp />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">WhatsApp</div>
                    <div className="item-value">
                      <a
                        href="https://wa.me/9187696393"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        +91 91876 96393
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaGlobe />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Website</div>
                    <div className="item-value">
                      <a
                        href="https://agents.fastays.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        agents.fastays.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {OFFICES[selectedOfficeIndex].id === 'mumbai' && (
              <div className="contact-methods">
                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FiPhone />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Call us</div>
                    <div className="item-value">
                      <a href="tel:+919187696392" className="phone-number">
                        +91 91876 96390
                      </a>
                      <a href="tel:+919187696394" className="phone-number">
                        +91 91876 96392
                      </a>
                      <a href="tel:+918197810030" className="phone-number">
                        +91 81978 10030
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FiMail />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Email us</div>
                    <div className="item-value">
                      <a href="mailto:info@fastays.com">info@fastays.com</a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaWhatsapp />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">WhatsApp</div>
                    <div className="item-value">
                      <a
                        href="https://wa.me/919187696392"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        +91 91876 96392
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaGlobe />
                  </div>
                  <div className="contact-item-details">
                    <div className="item-title">Website</div>
                    <div className="item-value">
                      <a
                        href="https://agents.fastays.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        agents.fastays.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Map Column */}
          <div className="map-column">
            <a
              href={currentOffice.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="open-maps-btn"
            >
              Open in Maps <FiExternalLink />
            </a>
            <div className="map-wrapper">
              <ContactLocationMap
                key={currentOffice.id}
                lat={currentOffice.lat}
                lng={currentOffice.lng}
                name={`Fastays — ${currentOffice.city}`}
              />
            </div>
          </div>

          {/* Form Column */}
          {isEmpty(userAuth) && (
            <div className="form-column">
              <h2 className="form-title">Get in Touch</h2>
              <form onSubmit={handleSubmit} className="form-grid">
                <div
                  className={`custom-input-wrapper ${
                    validationErrors.name ? 'error' : ''
                  }`}
                >
                  <input
                    type="text"
                    placeholder="Name *"
                    value={formData.name}
                    onChange={(e) => handleUpdateField('name', e.target.value)}
                  />
                  {validationErrors.name && (
                    <small className="app-error-text">
                      {validationErrors.name}
                    </small>
                  )}
                </div>

                <div
                  className={`custom-input-wrapper ${
                    validationErrors.email ? 'error' : ''
                  }`}
                >
                  <input
                    type="email"
                    placeholder="Email id *"
                    value={formData.email}
                    onChange={(e) => handleUpdateField('email', e.target.value)}
                  />
                  {validationErrors.email && (
                    <small className="app-error-text">
                      {validationErrors.email}
                    </small>
                  )}
                </div>

                <div
                  className={`custom-input-wrapper ${
                    validationErrors.number ? 'error' : ''
                  }`}
                >
                  <input
                    type="tel"
                    placeholder="Mobile No *"
                    value={formData.number}
                    onChange={(e) =>
                      handleUpdateField('number', e.target.value)
                    }
                  />
                  {validationErrors.number && (
                    <small className="app-error-text">
                      {validationErrors.number}
                    </small>
                  )}
                </div>

                <div className="app-select-wrapper">
                  <AppSelect
                    name="service"
                    selectedValue={formData.service}
                    options={serviceOptions}
                    onSelect={handleSelectService}
                    placeholder="Choose Services *"
                    isClearable={false}
                    errorText={validationErrors.service}
                  />
                </div>

                <div
                  className={`custom-input-wrapper ${
                    validationErrors.querymessage ? 'error' : ''
                  }`}
                >
                  <textarea
                    placeholder="Type Your Message"
                    value={formData.querymessage}
                    onChange={(e) =>
                      handleUpdateField('querymessage', e.target.value)
                    }
                  />
                  {validationErrors.querymessage && (
                    <small className="app-error-text">
                      {validationErrors.querymessage}
                    </small>
                  )}
                </div>

                <div className="checkbox-wrapper">
                  <label className="checkbox-container" htmlFor="agreeToTerms">
                    <input
                      id="agreeToTerms"
                      type="checkbox"
                      checked={formData.agreeToTerms}
                      onChange={(e) =>
                        handleUpdateField('agreeToTerms', e.target.checked)
                      }
                    />
                    <span className="checkbox-label">
                      By registering here, I agree to Fastays&apos;s{' '}
                      <a
                        href="/terms-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                  {validationErrors.agreeToTerms && (
                    <small className="app-error-text d-block mt-1">
                      {validationErrors.agreeToTerms}
                    </small>
                  )}
                </div>

                <button type="submit" className="submit-button">
                  Submit Now
                </button>
              </form>
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2 className="faq-title">Quick Answers About Reaching Us</h2>
          <div className="faq-accordion">
            {faqs.map((faq, index) => {
              const isExpanded = openFaqIndex === index;
              return (
                <div key={faq.question} className="faq-item">
                  <button
                    type="button"
                    className="faq-question-row"
                    onClick={() => toggleFaq(index)}
                  >
                    <h4 className="faq-question">{faq.question}</h4>
                    <span
                      className={`faq-toggle-btn ${
                        isExpanded ? 'expanded' : 'collapsed'
                      }`}
                    />
                  </button>
                  <div
                    className={`faq-answer-wrapper ${isExpanded ? 'open' : ''}`}
                  >
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA Footer Section */}
        {isEmpty(userAuth) && (
          <section className="partner-cta-box">
            <div className="cta-content">
              <h2 className="cta-title">Not A Fastays Partner Yet?</h2>
              <p className="cta-desc">
                Register as an agent and start booking at B2B rates today.
              </p>
              <div className="cta-buttons">
                <a href="/agent-registration" className="btn-become-agent">
                  Become an Agent
                </a>
                <a href="tel:+919187696392" className="btn-call-us">
                  Call Us First
                </a>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export default ContactUsPage;
